import { Box, Button, Grid, List, ListItem, Stack, TextField, Typography } from '@mui/material';
import Map, { getCity, getCountry, getDistrict, getLine, getPostalCode, getState } from './Map';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, useJsApiLoader } from '@react-google-maps/api';
import useAddEntityRequestStates from 'src/hooks/useAddEntityRequestStates';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import uuidv4 from 'src/utils/uuidv4';
import { Address, Extension } from 'src/careflow/ph-core/fhir-client/resources';
import AddressAutoComplete, { PlaceType } from './AddressAutoComplete';
import CancelButton from 'src/careflow/common/CancelButton';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { useUnsavedCloseGuard } from 'src/components/DialogCloseButton';
import {
  transformGooglePlaceToFhirAddress,
  getVisibleAdministrativeFields,
  filterAddressForFhirSave,
  getAddressFieldValue,
  AdministrativeField,
} from 'src/utils/addressMapping';

const AddAddress = ({
  handleClose,
  handleSave,
  isEditable,
  externalAddress,
  childrenTop,
  childrenBottom,
  disabled,
  configurations,
  externalError,
  externalUse,
  onDirtyChange,
}: any) => {
  const JsOptions = useMemo<any>(
    () => ({
      id: 'google-map-script',
      googleMapsApiKey: configurations?.googleMapsApiKey || '',
      libraries: ['places'],
    }),
    []
  );
  const { isLoaded } = useJsApiLoader(JsOptions);

  const [addressData, setAddressData] = useState<Address | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<PlaceType | null>(null);
  const [markerPosition, setMarkerPosition] = useState({
    lat: '',
    lng: '',
  });
  const [{ error }, { setError }] = useAddEntityRequestStates();
  const [useApi, setUseApi] = useState<boolean>(false);
  const { i18n } = useLocales();
  const { placesService } = usePlacesService({
    apiKey: configurations?.googleMapsApiKey,
  });
  const { componentsData } = useTenantConfigData();
  const administrativeFields = getVisibleAdministrativeFields(
    componentsData?.patient?.address || {}
  );
  const addressMappingConfig = componentsData?.patient?.address;

  const updateAddressData = (callback: (prevState: Address | null) => Address) => {
    if (!isEditable) return;
    if (callback)
      setAddressData((data) => ({
        ...data,
        ...callback(data),
      }));
  };

  useEffect(() => {
    if (isEmpty(externalAddress)) return;
    const { id, city, district, postalCode, state, country, line, use, extension } =
      externalAddress ?? {};
    const latitude = extension?.[0]?.extension?.find((e) => e.url === 'latitude').valueDecimal;
    const longitude = extension?.[0]?.extension?.find((e) => e.url === 'longitude').valueDecimal;

    if (latitude && longitude) setUseApi(true);

    setAddressData({
      id: id || '',
      city: city || '',
      district: district || '',
      line: line || [],
      postalCode: postalCode || '',
      state: state || '',
      country: country || '',
      use: use || '',
      extension,
    });
  }, [externalAddress]);

  // Baseline for detecting user edits (unsaved changes):
  // - Editing an existing address: snapshot the first loaded addressData.
  // - Adding a new address (empty externalAddress): baseline is "no data", so any
  //   entered data is dirty.
  const initialSnapshotRef = useRef<string | null>(null);
  if (initialSnapshotRef.current === null) {
    if (isEmpty(externalAddress)) {
      initialSnapshotRef.current = JSON.stringify(null);
    } else if (addressData) {
      initialSnapshotRef.current = JSON.stringify(addressData);
    }
  }
  const isDirty =
    initialSnapshotRef.current !== null &&
    initialSnapshotRef.current !== JSON.stringify(addressData ?? null);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  const requestClose = useUnsavedCloseGuard(handleClose, isDirty);

  const handleAddressLineChange =
    (lineNumber: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateAddressData((address) => {
        const lines = [...(address?.line ?? [])];
        lines[lineNumber] = event.target.value;
        return { line: lines };
      });
      setError(null);
    };

  const handleFieldChange =
    (fieldName: keyof Address) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateAddressData((_) => ({
        [fieldName]: event.target.value,
      }));
      setError(null);
    };

  const handleAdministrativeFieldChange =
    (field: AdministrativeField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      updateAddressData((currentAddress) => {
        const address = currentAddress || ({ line: [], use: externalUse ?? 'home' } as Address);

        if (field.fhirField?.startsWith('extension.') && field.extensionUrl) {
          const updatedAddress = { ...address };

          if (!updatedAddress.extension) {
            updatedAddress.extension = [];
          }

          const extension = {
            url: field.extensionUrl,
            valueString: value,
          } as Extension;

          const extensionIndex = updatedAddress.extension.findIndex(
            (ext) => ext.url === field.extensionUrl
          );

          if (extensionIndex >= 0) {
            updatedAddress.extension[extensionIndex] = extension;
          } else {
            updatedAddress.extension.push(extension);
          }

          return updatedAddress;
        }

        return {
          ...address,
          [field.fhirField as keyof Address]: value,
        } as Address;
      });

      setError(null);
    };

  const onPlaceSelected = (place: any) => {
    const addressArray = place.address_components;
    const line = getLine(addressArray);
    const city = getCity(addressArray);
    const state = getState(addressArray);
    const postalCode = getPostalCode(addressArray);
    const country = getCountry(addressArray);

    // Create base address with standard fields
    const baseAddress = {
      id: place.place_id,
      line: [line],
      city: city || '',
      state: state || '',
      postalCode: postalCode || '',
      country: country || '',
      use: externalUse ?? 'home',
      extension: [
        {
          extension: [
            {
              url: 'identifier',
              valueString: place.place_id,
            },
            {
              url: 'valid',
              valueBoolean: true,
            },
            {
              url: 'address',
              valueString: line,
            },
            {
              url: 'latitude',
              valueDecimal: place.geometry.location.lat(),
            },
            {
              url: 'longitude',
              valueDecimal: place.geometry.location.lng(),
            },
          ],
          url: `${configurations?.fhir?.baseUrl}/StructureDefinition/PH-address-valid`,
        },
      ],
    };

    // Transform using administrative fields configuration
    const transformedAddress = transformGooglePlaceToFhirAddress(
      place,
      addressMappingConfig || {},
      baseAddress as Partial<Address>
    );

    setUseApi(true);
    setAddressData(transformedAddress);
    setMarkerPosition({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    });
  };

  const validateRequiredFields = (payload: any) => {
    let isValid = true;

    // Check standard required fields
    const standardFieldsValid = !(
      isEmpty(payload?.id) ||
      isEmpty(payload?.line?.[0]) ||
      isEmpty(payload?.city) ||
      isEmpty(payload?.state) ||
      isEmpty(payload?.postalCode)
    );

    const adminFieldsValid = administrativeFields.every((field) => {
      const fieldValue = getAddressFieldValue(payload, field);
      return !isEmpty(fieldValue);
    });

    if (!standardFieldsValid || !adminFieldsValid) {
      setError('Please, fill all required fields');
      isValid = false;
    }
    return isValid;
  };

  const saveAddress = () => {
    if (!useApi && !!addressData) {
      delete addressData?.extension;
      addressData!.id = uuidv4();
    }

    if (!validateRequiredFields(addressData)) return;

    // Filter out fields that shouldn't be saved to FHIR based on configuration
    const addressToSave = addressMappingConfig
      ? filterAddressForFhirSave(addressData!, addressMappingConfig)
      : addressData;

    handleSave(addressToSave);
    setUseApi(false);
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (selectedAddress !== null) {
      placesService?.getDetails(
        {
          placeId: selectedAddress.place_id,
        },
        onPlaceSelected
      );
    }
  }, [selectedAddress, isLoaded]);

  useEffect(() => {
    if (externalError) setError('Please, fill all required fields');
    else setError(null);
  }, [externalError]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={5} style={{ paddingLeft: 0, paddingTop: 0 }}>
          <List>
            {childrenTop}
            <ListItem>
              {isLoaded && (
                <AddressAutoComplete
                  address={selectedAddress}
                  onAddressChange={(value) => {
                    setSelectedAddress(value);
                    setError(null);
                  }}
                />
              )}
            </ListItem>

            <ListItem>
              <TextField
                label={`${i18n('addAddress.streetAddress', 'crs')}*`}
                value={addressData?.line?.[0] || ''}
                onChange={handleAddressLineChange(0)}
                fullWidth
                error={!!error && isEmpty(addressData?.line?.[0]) ? true : false}
              />
            </ListItem>

            <ListItem>
              <TextField
                label={i18n('addAddress.unitSuitFloor', 'crs')}
                value={addressData?.line?.[1] || ''}
                onChange={handleAddressLineChange(1)}
                fullWidth
              />
            </ListItem>

            <ListItem>
              <TextField
                label={`${i18n('addAddress.city', 'crs')}*`}
                value={addressData?.city || ''}
                onChange={handleFieldChange('city')}
                fullWidth
                error={!!error && !addressData?.city ? true : false}
              />
            </ListItem>

            {administrativeFields.map((field) => {
              const fieldValue = addressData ? getAddressFieldValue(addressData, field) : '';
              const hasValue = !!fieldValue;

              return (
                <ListItem key={field.name}>
                  <TextField
                    label={`${field.label || field.name}*`}
                    value={fieldValue || ''}
                    onChange={handleAdministrativeFieldChange(field)}
                    fullWidth
                    error={!!error && !hasValue ? true : false}
                  />
                </ListItem>
              );
            })}

            <ListItem>
              <TextField
                label={`${i18n('addAddress.state', 'crs')}*`}
                value={addressData?.state || ''}
                onChange={handleFieldChange('state')}
                fullWidth
                error={!!error && !addressData?.state ? true : false}
              />
            </ListItem>

            <ListItem>
              <TextField
                label={`${i18n('addAddress.zipCode', 'crs')}*`}
                value={addressData?.postalCode || ''}
                onChange={handleFieldChange('postalCode')}
                fullWidth
                error={!!error && !addressData?.postalCode ? true : false}
              />
            </ListItem>
            {childrenBottom}
          </List>
        </Grid>

        <Grid item xs={7} style={{ paddingLeft: 0, paddingTop: 0, marginTop: 1 }}>
          <Map
            center={{ lat: parseFloat(markerPosition.lat), lng: parseFloat(markerPosition.lng) }}
            isLoaded={isLoaded}
          >
            {markerPosition.lat !== '' && markerPosition.lng !== '' ? (
              <Marker
                position={{
                  lat: parseFloat(markerPosition.lat),
                  lng: parseFloat(markerPosition.lng),
                }}
              />
            ) : null}
          </Map>
        </Grid>

        {error && (
          <Grid item xs={12} display={'flex'} justifyContent={'center'}>
            <Typography sx={{ color: '#FF4842' }}>{error}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 0.5 }}>
            <CancelButton onClick={() => void requestClose()}>{i18n('cancel')}</CancelButton>
            <Button variant="contained" onClick={saveAddress} disabled={disabled}>
              {i18n('saveChanges')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddAddress;
